import React from 'react';
import { number, string } from 'prop-types';
import { FormattedMessage } from '../../../util/reactIntl';

import css from './ImpactBanner.module.css';

/**
 * ImpactBanner — static "trust" banner shown above the search results.
 *
 * Renders a fixed social-proof strip — "Your booking supports local projects"
 * on the left, and hardcoded Partners / Countries / Travelers / Rating stats on
 * the right. Copy is localized through the `SearchPage.trustBanner*` keys.
 *
 * Rendered by both SearchPageWithMap.js and SearchPageWithGrid.js. Hidden on
 * mobile (≤767px) via the stylesheet.
 */

const ShieldCheckIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24">
    <path
      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2.8L14.8 8.45L21 9.35L16.5 13.75L17.55 20L12 17.05L6.45 20L7.5 13.75L3 9.35L9.2 8.45L12 2.8Z"
      fill="#FFC83D"
    />
  </svg>
);

const ImpactBanner = props => {
  const { className, resultCount } = props;
  const rootClasses = className ? `${css.searchTrustBanner} ${className}` : css.searchTrustBanner;

  // Partner count mirrors the current results count (the "X Ergebnisse" total),
  // so a Namibia search showing 25 results reads "25 Partner". Falls back to the
  // static brand figure while loading or when there's no valid count yet.
  const partnerCount =
    typeof resultCount === 'number' && resultCount > 0
      ? resultCount.toLocaleString('de-DE')
      : '453';

  return (
    <div
      className={rootClasses}
      data-elb="searchp"
      data-elb-searchp={`name:impactBanner;partners:${partnerCount};`}
      data-elbaction="visible:vis impactBanner"
    >
      <div className={css.searchTrustMain}>
        <span className={css.searchTrustIcon} aria-hidden="true">
          <ShieldCheckIcon />
        </span>
        <span>
          <FormattedMessage id="SearchPage.trustBannerText" />
        </span>
      </div>

      <div className={css.searchTrustStats}>
        <span>
          <strong>{453}</strong> <FormattedMessage id="SearchPage.trustBannerPartners" />
        </span>
        <span>
          <strong>44</strong> <FormattedMessage id="SearchPage.trustBannerCountries" />
        </span>
        <span>
          <strong>5.000+</strong> <FormattedMessage id="SearchPage.trustBannerTravelers" />
        </span>
        <span className={css.searchTrustRating}>
          <span className={css.searchTrustStar} aria-hidden="true">
            <StarIcon />
          </span>
          <strong>4,9</strong> <FormattedMessage id="SearchPage.trustBannerRating" />
        </span>
      </div>
    </div>
  );
};

ImpactBanner.defaultProps = {
  className: null,
  resultCount: null,
};

ImpactBanner.propTypes = {
  // Optional extra class on the root element.
  className: string,
  // Current search results total; drives the Partner stat when > 0.
  resultCount: number,
};

export default ImpactBanner;
